import type { TFunction } from "i18next";
import type { ChipType } from "../Chips/Chips";
import type {
  NumericFilterCriterion,
  NumericFilterOperator,
} from "../../types/types";

// Facet value types whose filter value is the option's id rather than its
// name (the name is display-only). Add a type here to make it id-keyed
// everywhere this file's helpers are used, instead of editing every call site.
const ID_KEYED_FACET_TYPES = new Set(["user"]);

export function isIdKeyedFacetType(type?: string): boolean {
  return !!type && ID_KEYED_FACET_TYPES.has(type);
}

const NUMERIC_OPERATORS: NumericFilterOperator[] = [
  "is",
  "is_exactly",
  "between",
  "overlaps",
];

/**
 * AP-1016 (Advanced Filters) — whether an operator takes a Min/Max pair
 * (`between`/`overlaps`) rather than a single value (`is`/`is_exactly`).
 * Per the Confluence spec's Operators & Match Rules, a range operator's
 * Min and Max are independently optional — leaving one blank makes it an
 * open-ended "at least"/"at most" query — but at least one of the two must
 * be filled; see `hasNumericBound`.
 */
export function isRangeOperator(operator: NumericFilterOperator): boolean {
  return operator === "between" || operator === "overlaps";
}

/**
 * A range criterion is valid once at least one bound is filled — both
 * blank isn't a searchable criterion. Shared by encode/decode and the
 * input's Apply-button gating so "at least one bound" is defined once.
 */
export function hasNumericBound(min?: number, max?: number): boolean {
  return min !== undefined || max !== undefined;
}

/**
 * FE-internal only — encodes a numeric filter criterion into a single string
 * so it can round-trip through the existing `{[key]: string[]}` filter-state
 * shape (URL params / sessionStorage overflow) without any changes to
 * `filterStateManager.ts`'s load/save machinery — the encoded string is
 * stored as the sole element of that filter key's array. This is purely the
 * browser-visible/shareable URL's own encoding; it is decoupled from — and
 * does not need to match — the backend wire contract (see
 * `compileNumericFilterQueryParams` below for the actual API request shape,
 * per docs/plans/NUMERIC_FILTERS_FE_SPEC.md).
 */
export function encodeNumericFilterCriterion(
  criterion: NumericFilterCriterion
): string {
  return criterion.operator === "between" || criterion.operator === "overlaps"
    ? `${criterion.operator}:${criterion.min ?? ""},${criterion.max ?? ""}`
    : `${criterion.operator}:${criterion.value}`;
}

/**
 * Inverse of `encodeNumericFilterCriterion`; returns null for malformed
 * input — including a range with BOTH bounds blank (one blank is a valid
 * open-ended "at least"/"at most" query, see `hasNumericBound`).
 */
export function decodeNumericFilterCriterion(
  raw: string | undefined
): NumericFilterCriterion | null {
  if (!raw) return null;
  const [operator, rest] = raw.split(":");
  if (!NUMERIC_OPERATORS.includes(operator as NumericFilterOperator) || !rest) {
    return null;
  }

  if (operator === "between" || operator === "overlaps") {
    const [minRaw, maxRaw] = rest.split(",");
    // `Number("")` is 0, not NaN — guard explicitly so a blank bound
    // decodes to `undefined` (open-ended) rather than 0.
    const min = minRaw ? Number(minRaw) : undefined;
    const max = maxRaw ? Number(maxRaw) : undefined;
    if (min !== undefined && !Number.isFinite(min)) return null;
    if (max !== undefined && !Number.isFinite(max)) return null;
    return hasNumericBound(min, max) ? { operator, min, max } : null;
  }

  const value = Number(rest);
  return Number.isFinite(value)
    ? { operator: operator as "is" | "is_exactly", value }
    : null;
}

/**
 * Human-readable label for an applied numeric criterion, e.g. "100" (Is /
 * Is exactly) / "At least 20" / "At most 5" / "Between 50 and 250" —
 * matches the natural-language applied-filter tag copy in the Q3 Figma file.
 * A range criterion with only one bound filled (see `hasNumericBound`) reads
 * as "At least"/"At most" rather than "Between" — there's no separate
 * operator for it anymore, per the Confluence spec's Operators & Match Rules.
 */
export function formatNumericFilterCriterion(
  criterion: NumericFilterCriterion,
  t: TFunction
): string {
  switch (criterion.operator) {
    case "is":
    case "is_exactly":
      return `${criterion.value}`;
    case "between":
    case "overlaps": {
      const { min, max } = criterion;
      if (min !== undefined && max !== undefined) {
        return t("Between {{min}} and {{max}}", { min, max }) as string;
      }
      if (min !== undefined) {
        return t("At least {{value}}", { value: min }) as string;
      }
      return t("At most {{value}}", { value: max }) as string;
    }
  }
}

/**
 * A Min/Max pair wouldn't just return zero results, it'd be nonsensical to
 * ask for — the BE spec explicitly asks the FE to prevent submitting one
 * (§2: "an inverted range... is indistinguishable to the user from a
 * genuinely empty [result]").
 */
export function isValidNumericRange(min: number, max: number): boolean {
  return min <= max;
}

/**
 * Compiles a numeric filter criterion into the flat, ready-to-append
 * query-string params the backend expects — `<filterType>_op` plus either
 * `<filterType>_value` (single-value operators) or `<filterType>_min`
 * and/or `<filterType>_max` (range operators; only the filled bound(s) are
 * sent, per the Confluence spec's Operators & Match Rules — "fields left
 * empty are not searched"). `_op` is always required and sent explicitly —
 * it is never inferred from which value params are present.
 */
export function compileNumericFilterQueryParams(
  filterType: string,
  criterion: NumericFilterCriterion
): Record<string, string> {
  if (criterion.operator === "between" || criterion.operator === "overlaps") {
    const params: Record<string, string> = {
      [`${filterType}_op`]: criterion.operator,
    };
    if (criterion.min !== undefined) {
      params[`${filterType}_min`] = String(criterion.min);
    }
    if (criterion.max !== undefined) {
      params[`${filterType}_max`] = String(criterion.max);
    }
    return params;
  }
  return {
    [`${filterType}_op`]: criterion.operator,
    [`${filterType}_value`]: String(criterion.value),
  };
}

/**
 * Expands every numeric-filter entry in a filter-state map into its
 * compiled wire keys (`compileNumericFilterQueryParams`), leaving every
 * other entry untouched — the shared building block for any surface that
 * turns FE filter state into request query params outside of
 * `convertToWildcardFormat`'s wildcard/exclude POST-body path. Used by
 * `applyFiltersToURL` (GET-based pages, e.g. MDM's EntityListPage.tsx) and
 * FiltersFlyBar's own MDM entity-filters metadata fetch.
 */
export function expandNumericFilterParams(
  filtersFromUrl: Record<string, string[]>
): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  Object.entries(filtersFromUrl).forEach(([key, values]) => {
    if (values.length === 1) {
      const criterion = decodeNumericFilterCriterion(values[0]);
      if (criterion) {
        Object.entries(compileNumericFilterQueryParams(key, criterion)).forEach(
          ([paramKey, paramValue]) => {
            result[paramKey] = [paramValue];
          }
        );
        return;
      }
    }
    result[key] = values;
  });
  return result;
}

export function chipsToStringArray(chips: ChipType[]) {
  // `user`-type facet values are matched by id on the backend (the name is only
  // for display); send the id for those and the name for everything else.
  return chips.map((chip) =>
    isIdKeyedFacetType(chip.type) && chip.id != null
      ? String(chip.id)
      : chip.name
  );
}

/**
 * Two facet options are the same selection if they share an id (ids aren't
 * guaranteed unique-by-name — e.g. two "user" options can share a display
 * name), falling back to name when either side has no id.
 */
export function isSameFacetOption(
  a: Pick<ChipType, "id" | "name">,
  b: Pick<ChipType, "id" | "name">
): boolean {
  return a.id != null && b.id != null ? a.id === b.id : a.name === b.name;
}

/**
 * Whether a raw URL/query string value refers to this facet option — either
 * because it's the option's name, or (for id-keyed facets, e.g. `user`) its
 * id. Used to reconcile a facet option against the applied filter values,
 * which may store either depending on the facet's value type.
 */
export function facetOptionValueMatches(
  option: Pick<ChipType, "id" | "name">,
  value: unknown
): boolean {
  return (
    typeof value === "string" &&
    (option.name === value ||
      (option.id != null && String(option.id) === value))
  );
}
